import React, { useState, useEffect } from 'react';
import { SbModal, SbForm, SbButton } from '../../../../uiCore';
import CoreMarkdownEditor from '../../../../Components/SbMarkdownEditor/CoreMarkdownEditor';
import { VIEWMODE } from '../../../../constants/markdown.constants';
import { ModalDescription, FormatGuideBox, CharacterCounter } from '../../AdminSetup.styles';

const toPlainText = (s = '') =>
  s.replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCodePoint(parseInt(h, 16)))
   .replace(/[*_~`]/g, '')
   .trim();

const ValuesCultureModal = ({ visible, onClose, initialData, onSave, cardKey }) => {
  const [form] = SbForm.useForm();
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [editorKey, setEditorKey] = useState(0);

  // Convert object to rich text dynamically
  const objectToRichText = (obj) => {
    if (!obj || typeof obj !== 'object') return '';
    
    let richText = '';
    
    Object.entries(obj).forEach(([key, value]) => {
      // Convert snake_case to Title Case for headings
      const heading = key.split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
      
      richText += `## ${heading}\n\n`;
      
      if (Array.isArray(value)) {
        value.forEach(item => {
          richText += `• ${item}\n`;
        });
        richText += '\n';
      } else if (typeof value === 'object' && value !== null) {
        // Nested object - create sub-headings
        Object.entries(value).forEach(([subKey, subValue]) => {
          const subHeading = subKey.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' ');
          richText += `### ${subHeading}\n${subValue}\n\n`;
        });
      } else {
        richText += `${value}\n\n`;
      }
    });
    
    return richText.trim();
  };

  // Parse rich text back to object dynamically
  const richTextToObject = (text) => {
    if (!text) return {};
    
    const lines = text.split('\n');
    const result = {};
    let currentKey = null;
    let currentContent = [];
    let isArray = false;
    
    for (let line of lines) {
      const trimmedLine = line.trim();
      
      // Check for heading (## or ###)
      if (trimmedLine.startsWith('## ') || trimmedLine.startsWith('### ')) {
        // Save previous section
        if (currentKey) {
          const snakeKey = currentKey.toLowerCase().replace(/\s+/g, '_');
          if (isArray) {
            result[snakeKey] = currentContent.filter(c => c);
          } else {
            result[snakeKey] = currentContent.join('\n').trim();
          }
        }
        
        // Start new section
        currentKey = trimmedLine.replace(/^#{2,3}\s+/, '');
        currentContent = [];
        isArray = false;
      } else if (trimmedLine.startsWith('• ') || trimmedLine.startsWith('- ')) {
        // Array item
        isArray = true;
        currentContent.push(toPlainText(trimmedLine.replace(/^[•\-]\s*/, '')));
      } else if (trimmedLine) {
        currentContent.push(toPlainText(trimmedLine));
      }
    }
    
    // Save last section
    if (currentKey) {
      const snakeKey = currentKey.toLowerCase().replace(/\s+/g, '_');
      if (isArray) {
        result[snakeKey] = currentContent.filter(c => c);
      } else {
        result[snakeKey] = currentContent.join('\n').trim();
      }
    }
    
    return result;
  };

  useEffect(() => {
    if (visible && initialData?.culture_values_beliefs) {
      const richText = objectToRichText(initialData.culture_values_beliefs);
      if (richText) {
        form.setFieldsValue({ cultureValuesBeliefs: richText });
        setCharCount(richText.length);
        setEditorKey(prev => prev + 1); // Force editor to remount with new data
      }
    }
  }, [visible, initialData, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const parsedData = richTextToObject(values.cultureValuesBeliefs);
      const oldData = initialData?.culture_values_beliefs || {};
      
      // Check if anything changed
      const hasChanges = JSON.stringify(parsedData) !== JSON.stringify(oldData);

      if (!hasChanges) {
        onClose();
        return;
      }

      // Send the parsed data as culture_values_beliefs
      const payload = {
        culture_values_beliefs: parsedData
      };

      const result = await onSave(cardKey, payload);
      if (result.success) onClose();
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SbModal
      title="Values & Culture"
      open={visible}
      onCancel={() => { form.resetFields(); setCharCount(0); onClose(); }}
      width={700}
      footer={[
        <SbButton key="cancel" buttonType="secondary" onClick={() => { form.resetFields(); setCharCount(0); onClose(); }}>Cancel</SbButton>,
        <SbButton key="save" buttonType="primary" loading={loading} onClick={handleSave}>Save</SbButton>
      ]}
    >
      <ModalDescription>
        Describe your organization's culture, core values and beliefs. This is embedded into JD generation and AI outputs to ensure cultural alignment.
      </ModalDescription>

      <FormatGuideBox>
        <span className="guide-title">Format Guide:</span>
        <span className="guide-item">• Use the toolbar to format text with bold, italics, bullets, etc.</span>
        <span className="guide-item">• Start sections with headings: <code>Culture Description</code>, <code>Core Values</code>, <code>Beliefs</code></span>
        <span className="guide-item">• Minimum 3 items required for Core Values and Beliefs</span>
      </FormatGuideBox>
      
      <SbForm form={form} layout="vertical">
        <SbForm.Item
          name="cultureValuesBeliefs"
          rules={[
            { required: true, message: 'Please enter culture, values and beliefs' },
            {
              validator: (_, value) => {
                if (!value || !value.trim()) {
                  return Promise.reject('Please enter some content');
                }

                if (value.length > 4000) {
                  return Promise.reject('Maximum 4000 characters');
                }

                const parsed = richTextToObject(value);

                // At least one field must have content
                if (Object.keys(parsed).length === 0) {
                  return Promise.reject('Please add at least one section with a heading');
                }

                // Validate that arrays have minimum items if they exist
                Object.entries(parsed).forEach(([key, val]) => {
                  if (Array.isArray(val) && val.length > 0 && val.length < 3) {
                    throw new Error(`Section "${key}" should have at least 3 items`);
                  }
                });

                return Promise.resolve();
              }
            }
          ]}
        >
          <CoreMarkdownEditor
            key={editorKey}
            initialMarkdown={form.getFieldValue('cultureValuesBeliefs') || ''}
            onChange={(value) => {
              form.setFieldsValue({ cultureValuesBeliefs: value });
              setCharCount(value?.length || 0);
            }}
            viewMode={VIEWMODE.EDIT}
          />
        </SbForm.Item>

        <CharacterCounter overLimit={charCount > 4000}>
          Total: {charCount} / 4000 characters
        </CharacterCounter>
      </SbForm>
    </SbModal>
  );
};

export default ValuesCultureModal;
